// DocumentGroupPanel.tsx — CTSK-498. Group + Collection labels on the
// DocumentDetail page (own Edit/Save, PATCH /documents/:id with the shared
// validator), plus the collapsible "Related documents" panel listing other
// documents in the same group / collection (capped at 100, indexed filters).
// CTSK-500 adds an "Uploaded together" list (documents that share this one's
// ingest batch id) to the same panel. CTSK-501 adds "Same folder" (documents
// that came from this one's exact source folder — not its subfolders, so it
// can differ from "Uploaded together"). Renders nothing unless grouping is live
// for the org.
// CTSK-505: the Related panel is fed by ONE GET /documents/:id/neighborhood
// (neighborhood.v1) instead of up to four /documents list calls. The panel is
// hidden when the response has no bases or the call fails (404 = gate off /
// not visible).

import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { normalizeGroupLabel, GROUP_LABEL_MAX_LENGTH } from '@captain-tasko/shared';
import type { Document, DocumentLabelSuggestion, NeighborhoodBasis, NeighborhoodData } from '@captain-tasko/shared';
import { apiSingle } from '../lib/api.js';
import { useDocumentGrouping } from '../hooks/useDocumentGrouping.js';
import { CollapsiblePanel } from './CollapsiblePanel.js';
import { toast } from './Toast.js';
import { batchLibraryHref, folderLibraryHref } from '../lib/ingest-batch.js';

export const RELATED_BY_LABEL_CAP = 100;

function LabelInput({
  label, kind, value, onChange,
}: { label: string; kind: 'group-names' | 'collection-names'; value: string; onChange: (v: string) => void }) {
  const listId = useId();
  const q = useQuery({
    queryKey: ['documents', 'label-suggestions', kind, value.trim()],
    queryFn: () => apiSingle<DocumentLabelSuggestion[]>(`/documents/${kind}`, {
      query: { prefix: value.trim() || undefined, limit: 20 },
    }),
    staleTime: 15_000,
    retry: 0,
  });
  const res = normalizeGroupLabel(value);
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <input
          type="text"
          list={listId}
          aria-label={label}
          aria-invalid={res.ok ? undefined : true}
          maxLength={GROUP_LABEL_MAX_LENGTH * 2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '100%', padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)' }}
        />
        <datalist id={listId}>
          {(q.data ?? []).map((s) => <option key={s.value} value={s.value}>{`${s.value} (${s.count})`}</option>)}
        </datalist>
        {!res.ok && <div role="alert" style={{ color: 'var(--danger-primary, #B3261E)', fontSize: 12 }}>{res.message}</div>}
      </dd>
    </>
  );
}

// One basis of the neighborhood response. `batch` (CTSK-500) filters on
// ?ingestBatch=; the id itself is an opaque ULID, so the heading shows
// "Uploaded together" rather than the value. `folder` (CTSK-501) filters on
// ?folder= (exact, case-sensitive) and shows the path.
function RelatedList({ basis }: { basis: NeighborhoodBasis }) {
  const kind = basis.basis;
  const rows = basis.items.slice(0, RELATED_BY_LABEL_CAP);
  const heading =
    kind === 'group' ? 'Same group'
      : kind === 'collection' ? 'Same collection'
        : kind === 'folder' ? 'Same folder'
          : 'Uploaded together';
  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        {kind === 'batch' ? heading : `${heading}: ${basis.label ?? basis.key}`} <span style={{ color: 'var(--text-muted)' }}>({basis.count})</span>
      </div>
      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
        {rows.map((r) => (
          <li key={r.id}><a href={`/documents/${r.id}`}>{r.title}</a></li>
        ))}
      </ul>
      {kind === 'batch' ? (
        <a href={batchLibraryHref(basis.key)} style={{ fontSize: 12 }}>View batch in library</a>
      ) : kind === 'folder' ? (
        <a href={folderLibraryHref(basis.key)} style={{ fontSize: 12 }}>View folder in library</a>
      ) : (
        <a href={`/documents?${kind}=${encodeURIComponent(basis.key)}`} style={{ fontSize: 12 }}>Open in library</a>
      )}
    </div>
  );
}

// Display order as shipped in CTSK-498/500/501 (the response order is the API's).
const DISPLAY_ORDER: NeighborhoodBasis['basis'][] = ['group', 'collection', 'batch', 'folder'];

export function DocumentGroupPanel({ doc }: { doc: Pick<Document, 'id' | 'etag' | 'groupName' | 'collectionName' | 'ingestBatchId' | 'sourceFolder'> }) {
  const grouping = useDocumentGrouping();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [group, setGroup] = useState(doc.groupName ?? '');
  const [collection, setCollection] = useState(doc.collectionName ?? '');
  useEffect(() => {
    setGroup(doc.groupName ?? '');
    setCollection(doc.collectionName ?? '');
  }, [doc.groupName, doc.collectionName, editing]);

  const save = useMutation({
    mutationFn: () => apiSingle<Document>(`/documents/${doc.id}`, {
      method: 'PATCH',
      // '' normalises to null server-side, which clears the label.
      body: { groupName: group, collectionName: collection },
      ifMatch: doc.etag,
    }),
    onSuccess: () => {
      toast.success('Saved');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['document', doc.id] });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const hasAnyLabel = Boolean(doc.groupName || doc.collectionName || doc.ingestBatchId || doc.sourceFolder);
  const hood = useQuery({
    queryKey: ['documents', 'neighborhood', doc.id, RELATED_BY_LABEL_CAP],
    queryFn: () => apiSingle<NeighborhoodData>(`/documents/${doc.id}/neighborhood`, { query: { limit: RELATED_BY_LABEL_CAP } }),
    enabled: grouping.enabled && hasAnyLabel,
    retry: 0,
  });

  if (!grouping.enabled) return null;
  const bases = DISPLAY_ORDER.flatMap((k) => hood.data?.bases.filter((b) => b.basis === k) ?? []);
  const invalid = !normalizeGroupLabel(group).ok || !normalizeGroupLabel(collection).ok;
  const dash = <span style={{ color: 'var(--text-muted)' }}>—</span>;

  return (
    <>
      <CollapsiblePanel
        storageKey="docdetail:grouping"
        title="Group & collection"
        headerExtras={!editing ? (
          <button type="button" className="btn btn--ghost" onClick={() => setEditing(true)}>Edit</button>
        ) : undefined}
      >
        <dl className="kv">
          {editing ? (
            <>
              <LabelInput label="Group" kind="group-names" value={group} onChange={setGroup} />
              <LabelInput label="Collection" kind="collection-names" value={collection} onChange={setCollection} />
            </>
          ) : (
            <>
              <dt>Group</dt><dd>{doc.groupName ?? dash}</dd>
              <dt>Collection</dt><dd>{doc.collectionName ?? dash}</dd>
              {doc.sourceFolder && <><dt>Folder</dt><dd data-testid="doc-source-folder" style={{ overflowWrap: 'anywhere' }}>{doc.sourceFolder}</dd></>}
            </>
          )}
        </dl>
        {editing && (
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={() => setEditing(false)} disabled={save.isPending}>Cancel</button>
            <button type="button" className="btn btn--primary" disabled={invalid || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </CollapsiblePanel>
      {bases.length > 0 && (
        <CollapsiblePanel
          storageKey="docdetail:related-by-label"
          title="Related documents"
          badge={`(${bases.reduce((n, b) => n + b.count, 0)})`}
          defaultCollapsed
          persist={false}
        >
          {bases.map((b) => <RelatedList key={b.basis} basis={b} />)}
        </CollapsiblePanel>
      )}
    </>
  );
}
